/* eslint-disable react-refresh/only-export-components */
import React, { useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import { DigitalSignatureNode } from "./digital-signature-node";
import { DigitalSignatureProvider } from "./context";
import { useDigitalSignatureNode } from "./hooks";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export const DigitalSignatureDetailsForm: React.FC = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || "Firma de documentos",
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );

  return (
    <DigitalSignatureModal
      title={title}
      setTitle={setTitle}
      modalState={modalState}
      setModalState={setModalState}
    />
  );
};

const DigitalSignatureModal: React.FC<{
  title: string;
  setTitle: (title: string) => void;
  modalState: "idle" | "close-warning";
  setModalState: (state: "idle" | "close-warning") => void;
}> = ({ title, setTitle, modalState, setModalState }) => {
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } = useNodeNameValidation(selectedNodeId);

  const { documentGroups, isLoadingSignatureTypes, isFormValid, saveNode } =
    useDigitalSignatureNode();

  const documents = documentGroups
    .flatMap((group) => group.items)
    .flatMap((item) => item)
    .filter((doc) => doc.selected);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={isFormValid && isNameValid(title)}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          saveNode(title);
          setNodeDetailsOpen(false);
          setSelectedNodeId();
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "signature",
            title: title,
            color: "action",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            rightContent: (
              <Button
                variant="quiet"
                size="sm"
                className="text-body-loud bg-neutral-object border border-neutral-quiet hover:bg-neutral-object-accent-silent"
                onClick={() => {
                  // TODO: Implementar preview functionality
                  console.log("Preview clicked");
                }}
              >
                Preview
              </Button>
            ),
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            !isLoadingSignatureTypes ? (
              <DigitalSignatureNode />
            ) : (
              <div>
                <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </p>
              </div>
            )
          }
          rightContent={
            <>
              <div className="h-full bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                {documents.length > 0 ? (
                  <InputOutput
                    nodeType={NodeTypes.SIGN_DOCUMENT}
                    showSteps={false}
                    nodeLabel={title}
                    variableType="file"
                    documents={documents}
                  />
                ) : (
                  <div className="h-full flex justify-center items-center flex-col m-auto">
                    <Heading variant="h6" className="text-on-disabled">
                      Objeto sin variables generadas
                    </Heading>
                    <span className="text-sm text-on-disabled">
                      Configura el objeto para poder visualizar las variables
                      generadas.
                    </span>
                  </div>
                )}
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const DigitalSignatureDetails: React.FC = () => {
  return (
    <DigitalSignatureProvider>
      <DigitalSignatureDetailsForm />
    </DigitalSignatureProvider>
  );
};

// Exportaciones adicionales para la historia de Storybook
export { DigitalSignatureNode } from "./digital-signature-node";
export { DigitalSignatureProvider } from "./context";
export { useDigitalSignatureNode } from "./hooks";
export type {
  SignatureType,
  FileToSign,
  Signer,
  DigitalSignatureData,
} from "./types";
export { signatureTypes, mockFilesToSign, mockSigners } from "./types";
